/* eslint-disable complexity */
/* eslint-disable no-console */
import {
    GLOBAL_EVENT_NAME,
    PARAM_METHOD,
    PARAM_REFERRER,
    PARAM_SIGN_UP,
    NEW_SESSION_TRACKED_ANALYTICS,
    PARAM_LOGGED_IN,
    PARAM_PROGRESSIVE,
} from './constants';
import { TBIDProfile } from './types';
import { isNewTBIDSession, deleteSearchParams, getUserProfile, triggerEvent } from './utils';

/**
 * Retrieve user profile information from UserInfo
 * @returns {} user profile info or empty obj
 *
 */
export const getProfileData = async (): Promise<TBIDProfile> => {
    // Getting profile data
    const profile = (await getUserProfile()).userInfo;

    const fullAPIProfileInfoDict: { [key: string]: any } = {
        company: 'CompanyName',
        id: 'user_id',
        newRegistration: 'newRegistration',
        orgID: 'organization_id',
        role: 'Role',
        relationship: 'RelationshipToSalesforce',
    };

    // map object... Default value = empty string
    return Object.keys(fullAPIProfileInfoDict).reduce((o: any, p: string) => {
        // If nothing comes back we use empty string as default, then if the attribute is in custom_attributes
        o[p] = profile[fullAPIProfileInfoDict[p]] ?? '';
        if (o[p] === '' && profile['custom_attributes']) {
            o[p] = profile['custom_attributes'][fullAPIProfileInfoDict[p]] ?? '';
        }
        return o;
    }, {});
};

/**
 * Track TBID logins on page load
 */
export const initTracking = (path: string = window.location.href): void => {
    window.addEventListener('load', emitAnalyticsEvent(path));
};

export function emitAnalyticsEvent(path: string): () => any {
    return async function () {
        const url = new URL(path);
        //Backend will always send signup param either set to 0 or 1 based on the whether it's a login or registration
        if (isNewTBIDSession(url)) {
            //signup=1 from the backend indicates that this is a new registration, so we trigger custEv_signUp event
            if (url.searchParams.get(PARAM_SIGN_UP) === '1') {
                await addLoginOrSignUpInfoToDataLayer(url.searchParams, 'custEv_signUp');
                //signup=0 from the backend indicates that this is a login, so we trigger custEv_userLogin event
            } else if (url.searchParams.get(PARAM_SIGN_UP) === '0') {
                await addLoginOrSignUpInfoToDataLayer(url.searchParams, 'custEv_userLogin');
            }
            // Once the events are triggered we delete the params method and referrer so that events are not re-triggered on a page refresh
            deleteSearchParams(url, [PARAM_LOGGED_IN, PARAM_SIGN_UP, PARAM_METHOD, PARAM_REFERRER, PARAM_PROGRESSIVE]);
        }
    };
}

/**
 * Add the user profile info to data layer
 */
export const addLoginOrSignUpInfoToDataLayer = async (urlParams: URLSearchParams, eventType: string): Promise<void> => {
    // check if we've already tracked this event
    if (window.sessionStorage.getItem(NEW_SESSION_TRACKED_ANALYTICS) !== 'true') {
        const userId = urlParams.get('userId');
        // Call getProfileData only if userId is not present in urlParams for idx registration redirect
        const info: any = !userId ? await getProfileData() : null;

        const method = urlParams.get(PARAM_METHOD);
        const urlEncoded = urlParams.get(PARAM_REFERRER);
        let url = '';
        let referrer = '';
        if (urlEncoded) {
            url = decodeURI(urlEncoded);
            referrer = new URL(url)?.origin;
        }

        //id object based on event type and availability of info
        const idObject = {
            company: eventType === 'custEv_userLogin' && info ? info.company : null,
            id: info ? info.id : userId,
            newRegistration: info ? info.newRegistration : null,
            orgID: info ? info.orgID : null,
            role: eventType === 'custEv_userLogin' && info ? info.role : null,
            relationship: eventType === 'custEv_userLogin' && info ? info.relationship : null,
        };

        const data = {
            event: eventType,
            experience: `page`,
            authenticationMethod: `tbid`,
            method,
            referrer,
            url,
            id: {
                tb: idObject,
            },
        };
        await triggerEvent(window.document as unknown as HTMLElement, GLOBAL_EVENT_NAME, data);
        // set session var to prevent duplicate tracking
        window.sessionStorage.setItem(NEW_SESSION_TRACKED_ANALYTICS, 'true');
    }
};
